import {
  Avatar,
  Badge,
  Card,
  HStack,
  Input,
  NativeSelect,
  Spacer,
  Stack,
  Text,
} from "@chakra-ui/react";
import * as React from "react";
import { Controller, useForm } from "react-hook-form";
import { LuUserPlus } from "react-icons/lu";
import { useNavigate, useParams } from "react-router-dom";

import {
  InviteWorkspaceMemberData,
  WorkspaceMemberDetails,
  getWorkspaceMembers,
  inviteWorkspaceMember,
  updateWorkspace,
} from "../../api/workspace";
import { Button } from "../../components/button";
import { Field } from "../../components/field";
import { InputGroup } from "../../components/input-group";
import SectionHeader from "../../components/section-header";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { capitalize } from "../../lib/capitalize";
import { checkUser } from "../../store/authSlice";
import { setSlug } from "../../store/workspaceSlice";
import { Workspace } from "../../types/workspace";

interface WorkspaceForm {
  name: string;
  slug: string;
}

export default function GeneralSettingsPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { workspace: slug } = useParams();
  const { workspaces } = useAppSelector((state) => state.auth.user);
  const workspace = React.useMemo(
    () => workspaces.find((w) => w.slug === slug),
    [slug, workspaces],
  );
  const [isUpdating, setIsUpdating] = React.useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<WorkspaceForm>({
    defaultValues: {
      name: workspace.name,
      slug: workspace.slug,
    },
  });
  const onSubmit = handleSubmit(
    async (data: Omit<Workspace, "domain" | "id">) => {
      setIsUpdating(true);
      try {
        const updatedWorkspace = { ...workspace, ...data };
        await updateWorkspace(workspace.slug, updatedWorkspace);
        await dispatch(checkUser()).unwrap();
        dispatch(setSlug(workspace.slug));
        navigate(`/workspaces/${updatedWorkspace.slug}/settings`);
      } finally {
        setIsUpdating(false);
      }
    },
  );
  return (
    <Stack gap="5">
      <Card.Root variant="elevated" maxW="xl">
        <Card.Header>
          <SectionHeader
            title="Workspace"
            description="Manage your workspace settings."
          />
        </Card.Header>
        <Card.Body>
          <form onSubmit={onSubmit}>
            <Stack gap="3">
              <Field
                label="Name"
                invalid={!!errors.name}
                errorText={errors.name?.message}
                orientation="horizontal"
              >
                <Input
                  {...register("name", { required: "A name is required." })}
                />
              </Field>
              <Field
                label="Slug"
                invalid={!!errors.slug}
                errorText={errors.slug?.message}
                orientation="horizontal"
              >
                <Input
                  {...register("slug", { required: "A slug is required." })}
                />
              </Field>
              <HStack>
                <Spacer />
                <Button loading={isUpdating} type="submit">
                  Update
                </Button>
              </HStack>
            </Stack>
          </form>
        </Card.Body>
      </Card.Root>
      {workspace && <WorkspaceMembersSection workspace={workspace} />}
    </Stack>
  );
}

interface WorkspaceMembersSectionProps {
  workspace: Workspace;
}

function WorkspaceMembersSection({ workspace }: WorkspaceMembersSectionProps) {
  const [isLoading, setIsLoading] = React.useState(true);
  const [members, setMembers] = React.useState<WorkspaceMemberDetails[]>([]);
  const loadMembers = React.useCallback(async () => {
    setIsLoading(true);
    try {
      setMembers(await getWorkspaceMembers(workspace.slug));
    } finally {
      setIsLoading(false);
    }
  }, [workspace]);
  const inviteMember = React.useCallback(
    async (data: InviteWorkspaceMemberData) => {
      setIsLoading(true);
      try {
        await inviteWorkspaceMember(workspace.slug, data);
        await loadMembers();
      } finally {
        setIsLoading(false);
      }
    },
    [loadMembers, workspace],
  );

  React.useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  return (
    <Card.Root bg="bg.muted" variant="elevated" maxW="xl">
      <Card.Header bg="bg" borderTopRadius="md">
        <SectionHeader
          title="Members"
          description="Manage who can access your workspace."
        />
      </Card.Header>
      <Card.Body bg="bg" borderBottomRadius="lg" borderBottomWidth="1">
        <InviteMemberView onSubmit={inviteMember} />
      </Card.Body>
      {!isLoading && (
        <Card.Footer borderBottomRadius="md" pt="4">
          <Stack gap="3">
            {members.map((m) => (
              <HStack key={m.username}>
                <HStack w={"300px" as any}>
                  <Avatar.Root size="sm">
                    <Avatar.Fallback name={m.username} />
                  </Avatar.Root>
                  <Text fontSize="sm" color="fg.muted">
                    {m.username}
                  </Text>
                </HStack>
                <Text fontSize="sm" w={"100px" as any}>
                  {capitalize(m.role)}
                </Text>
                <Badge colorPalette="gray" variant="outline">
                  {capitalize(m.status)}
                </Badge>
              </HStack>
            ))}
          </Stack>
        </Card.Footer>
      )}
    </Card.Root>
  );
}

interface InviteMemberForm {
  email: string;
  role: string;
}

interface InviteMemberViewProps {
  onSubmit: (data: InviteWorkspaceMemberData) => Promise<void>;
}

function InviteMemberView(props: InviteMemberViewProps) {
  const { control, register, handleSubmit } = useForm<InviteMemberForm>({
    defaultValues: { role: "member" },
  });
  const onSubmit = handleSubmit(({ email, role }) => {
    props.onSubmit({ email, role });
  });

  return (
    <form onSubmit={onSubmit} style={{ display: "flex", flex: "1" }}>
      <InputGroup
        flex="1"
        endElement={
          <HStack>
            <Controller
              name="role"
              control={control}
              render={({ field }) => (
                <NativeSelect.Root
                  me="-1"
                  onBlur={field.onBlur}
                  onChange={(e) => field.onChange((e.target as any).value)}
                  size="xs"
                  variant="plain"
                  w="auto"
                >
                  <NativeSelect.Field defaultValue="member" fontSize="sm">
                    <option value="member">Member</option>
                    <option value="admin">Admin</option>
                  </NativeSelect.Field>
                  <NativeSelect.Indicator />
                </NativeSelect.Root>
              )}
            />
            <Button mr="-2" size="xs" type="submit">
              <LuUserPlus /> Invite
            </Button>
          </HStack>
        }
      >
        <Input
          pe="0"
          placeholder="user@example.com"
          type="email"
          {...register("email", {
            required: "An email address is required.",
          })}
        />
      </InputGroup>
    </form>
  );
}
